/**
 * Stops authenticated API storms after session expiry / token mismatch.
 * Once marked, request interceptors refuse to send further calls (no network).
 */

export const AUTH_SESSION_EXPIRED_EVENT = "lms-auth-session-expired";

let expired = false;
let redirectScheduled = false;

export function isAuthSessionExpired(): boolean {
  return expired;
}

export function resetAuthSessionExpired(): void {
  expired = false;
  redirectScheduled = false;
}

export function markAuthSessionExpired(reason?: string): void {
  if (expired) return;
  expired = true;
  try {
    window.dispatchEvent(
      new CustomEvent(AUTH_SESSION_EXPIRED_EVENT, {
        detail: { reason: reason ?? "unauthorized" },
      }),
    );
  } catch {
    /* ignore */
  }
}

/** Clear storage once and navigate to login at most once per expiry. */
export function redirectToLoginAfterAuthFailure(clearUserData: () => void): void {
  markAuthSessionExpired("http-401");
  try {
    clearUserData();
  } catch {
    /* ignore */
  }
  if (redirectScheduled || typeof window === "undefined") return;
  redirectScheduled = true;
  window.location.href = "/";
}

export function isAuthHttpError(err: unknown): boolean {
  if (err == null || typeof err !== "object") return false;
  const status = (err as { response?: { status?: number }; status?: number })
    .response?.status ?? (err as { status?: number }).status;
  // 401 = dead session. 403 is often a business/authz deny (pause, role), not logout.
  return status === 401;
}

/** Reject before wire when session already known dead. */
export function rejectIfAuthSessionExpired(): Error | null {
  if (!expired) return null;
  return new Error("Auth session expired — request blocked");
}
